// components/layout/OrderAlert.jsx
// Admins: a banner that stays until dismissed for every unread new order (Uganda + DRC),
// a short chime when a new one arrives (mute toggle, remembered on this browser), the
// browser tab title "(n) New order", and optional desktop notifications.
// Browsers only allow sound after the first click on the page. Display only.
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrderAlerts } from '../../context/OrderAlertsContext';

const MUTE_KEY = 'dwi.orderAlerts.muted';
const readMuted = () => { try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; } };
const saveMuted = v => { try { localStorage.setItem(MUTE_KEY, v ? '1' : '0'); } catch { /* private window */ } };

// Two short tones — no sound file needed.
export function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return false;
    const ctx = new Ctx();
    [[880, 0], [1320, 0.18]].forEach(([freq, at]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.3);
      o.connect(g).connect(ctx.destination); o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + 0.32);
    });
    setTimeout(() => ctx.close && ctx.close(), 1000);
    return true;
  } catch { return false; }
}

const sectionFor = a => (a.market === 'DRC' ? 'drcshop' : 'orders');

export default function OrderAlert({ chime = playChime }) {
  const { alerts, fresh, count } = useOrderAlerts();
  const navigate = useNavigate();
  const [muted, setMuted] = useState(readMuted);
  const [dismissed, setDismissed] = useState(() => new Set());
  const baseTitle = useRef(typeof document !== 'undefined' ? document.title : '');

  // New arrival → chime + desktop notification (if allowed).
  useEffect(() => {
    if (!fresh.length) return;
    if (!muted) chime();
    try {
      if (window.Notification && Notification.permission === 'granted') {
        for (const a of fresh.slice(0, 3)) new Notification(a.market === 'DRC' ? 'New DRC order' : 'New Uganda order', { body: a.body });
      }
    } catch { /* not supported */ }
  }, [fresh]);   // only new arrivals chime (muted/chime read at that moment)

  // Tab title shows the count while there are unread new orders.
  useEffect(() => {
    document.title = count > 0 ? `(${count}) New order — ${baseTitle.current}` : baseTitle.current;
    return () => { document.title = baseTitle.current; };
  }, [count]);

  const shown = alerts.filter(a => !dismissed.has(String(a.id)));
  if (!shown.length) return null;
  const toggleMute = () => { setMuted(m => { saveMuted(!m); return !m; }); };
  const askDesktop = () => { try { window.Notification && Notification.requestPermission(); } catch { /* ignore */ } };

  return (
    <div className="sticky top-0 z-40 mb-3 rounded-xl border-2 border-red bg-red/15 p-3 shadow-card" role="alert" data-testid="order-alert">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <div className="font-black text-text-primary">🛒 {shown.length} new order{shown.length > 1 ? 's' : ''} — don’t miss the sale</div>
        <div className="flex gap-2 text-xs">
          <button className="underline text-text-secondary" onClick={toggleMute}>{muted ? '🔇 Sound off' : '🔔 Sound on'}</button>
          {typeof window !== 'undefined' && window.Notification && Notification.permission === 'default' &&
            <button className="underline text-text-secondary" onClick={askDesktop}>Desktop alerts</button>}
        </div>
      </div>
      {shown.slice(0, 3).map(a => (
        <div key={a.id} className="flex items-center justify-between gap-2 text-sm py-1 border-t border-red/30">
          <span className="text-text-primary"><strong>{a.market === 'DRC' ? '🇨🇩 DRC' : '🇺🇬 Uganda'}</strong> {a.body}</span>
          <span className="flex gap-2 shrink-0">
            <button className="px-2 py-1 rounded bg-red text-white text-xs font-bold" onClick={() => navigate(`/admin?tab=market&section=${sectionFor(a)}`)}>Open</button>
            <button className="px-2 py-1 rounded border border-border text-xs" onClick={() => setDismissed(s => new Set([...s, String(a.id)]))}>Hide</button>
          </span>
        </div>
      ))}
      {shown.length > 3 && <div className="text-xs text-text-secondary pt-1">…and {shown.length - 3} more — open Admin → DwiSend.</div>}
    </div>
  );
}
